import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import StickyPageHeader from '../common/StickyPageHeader';
import {
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Calendar
} from 'lucide-react';
import { useCalendarDataQuery, usePrefetchCalendarMonths, useCategoriesQuery } from '../../hooks/useQueries';
import { useConfig } from '../../context/ConfigContext';
import { getIconComponent } from '../common/IconPicker';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const SHORT_MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

// Check if we're on mobile (for prefetching adjacent months)
const isMobile = () => typeof window !== 'undefined' && window.innerWidth < 1280;

export default function CalendarPage() {
  const navigate = useNavigate();
  const { formatAmount, config } = useConfig();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionDirection, setTransitionDirection] = useState(null);
  const [autoSelectedToday, setAutoSelectedToday] = useState(false);
  const [isMobileView, setIsMobileView] = useState(isMobile());

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;

  // Track mobile state for prefetching
  useEffect(() => {
    const handleResize = () => setIsMobileView(isMobile());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get day names starting from configured week start
  const getDayNames = () => {
    const startDay = config.weekStartDay || 0;
    const fullDayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return [...fullDayNames.slice(startDay), ...fullDayNames.slice(0, startDay)];
  };

  const getShortDayNames = () => {
    const startDay = config.weekStartDay || 0;
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return [...dayNames.slice(startDay), ...dayNames.slice(0, startDay)];
  };

  // Get the original day-of-week index for coloring Sun/Sat
  const getDayOfWeekIndex = (gridIndex) => {
    const startDay = config.weekStartDay || 0;
    return (gridIndex + startDay) % 7;
  };

  // React Query hook - data is cached automatically
  const { data: calendarData, isLoading: loading } = useCalendarDataQuery(year, month);

  // Fetch categories for emoji icons
  const { data: categories = [] } = useCategoriesQuery();

  // Build category lookup map
  const categoryMap = useMemo(() => {
    const map = {};
    categories.forEach(cat => {
      map[cat.name] = { icon: cat.icon, color: cat.color };
    });
    return map;
  }, [categories]);

  // Prefetch adjacent months on mobile for instant swiping
  usePrefetchCalendarMonths(year, month, isMobileView);

  // Auto-select today's date when data loads, and update when data refreshes
  const today = new Date();
  const todayDate = today.getDate();
  const isCurrentMonth = today.getMonth() + 1 === month && today.getFullYear() === year;

  useEffect(() => {
    if (!loading && calendarData && isCurrentMonth) {
      const todayData = calendarData?.dailyData?.[todayDate.toString()];
      if (!autoSelectedToday) {
        // First load: auto-select today
        setSelectedDay({
          day: todayDate,
          income: todayData?.income || 0,
          expense: todayData?.expense || 0,
          transactions: todayData?.transactions || []
        });
        setAutoSelectedToday(true);
      } else if (selectedDay?.day === todayDate) {
        // Data refreshed while today is still selected: update transactions
        setSelectedDay(prev => ({
          ...prev,
          income: todayData?.income || 0,
          expense: todayData?.expense || 0,
          transactions: todayData?.transactions || []
        }));
      }
    }
  }, [loading, calendarData, isCurrentMonth, todayDate]);

  const animateMonthChange = useCallback((direction, newDate) => {
    setTransitionDirection(direction);
    setIsTransitioning(true);
    setSelectedDay(null);

    setTimeout(() => {
      setCurrentDate(newDate);
      setTransitionDirection(direction === 'left' ? 'enter-left' : 'enter-right');

      setTimeout(() => {
        setIsTransitioning(false);
        setTransitionDirection(null);
      }, 200);
    }, 150);
  }, []);

  const goToPreviousMonth = useCallback(() => {
    if (isTransitioning) return;
    animateMonthChange('right', new Date(year, month - 2, 1));
  }, [year, month, isTransitioning, animateMonthChange]);

  const goToNextMonth = useCallback(() => {
    if (isTransitioning) return;
    animateMonthChange('left', new Date(year, month, 1));
  }, [year, month, isTransitioning, animateMonthChange]);

  const goToToday = () => {
    if (isTransitioning) return;
    const newDate = new Date();
    setCurrentDate(newDate);
    const todayData = calendarData?.dailyData?.[newDate.getDate().toString()];
    setSelectedDay({
      day: newDate.getDate(),
      income: todayData?.income || 0,
      expense: todayData?.expense || 0,
      transactions: todayData?.transactions || []
    });
  };

  // Swipe gesture support for mobile
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const minSwipeDistance = 50;

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = null;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
    if (touchStartX.current && touchEndX.current) {
      const offset = (touchEndX.current - touchStartX.current) * 0.3;
      setSwipeOffset(offset);
    }
  };

  const handleTouchEnd = () => {
    setSwipeOffset(0);
    if (!touchStartX.current || !touchEndX.current) return;

    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNextMonth();
    } else if (isRightSwipe) {
      goToPreviousMonth();
    }

    touchStartX.current = null;
    touchEndX.current = null;
  };

  const getCalendarTransformStyle = () => {
    if (isTransitioning) {
      switch (transitionDirection) {
        case 'left':
          return { transform: 'translateX(-30%)', opacity: 0 };
        case 'right':
          return { transform: 'translateX(30%)', opacity: 0 };
        case 'enter-left':
        case 'enter-right':
          return { transform: 'translateX(0)', opacity: 1 };
        default:
          return {};
      }
    }
    if (swipeOffset !== 0) {
      return { transform: `translateX(${swipeOffset}px)` };
    }
    return {};
  };

  const handleDayClick = (day, dayData) => {
    setSelectedDay({ day, ...dayData });
  };

  // Navigate to transaction detail page
  const handleTransactionClick = (transaction) => {
    navigate(`/transactions/${transaction._id}`);
  };

  // Generate calendar grid
  const generateCalendarDays = () => {
    const firstDayOfMonth = new Date(year, month - 1, 1);
    const lastDayOfMonth = new Date(year, month, 0);
    const daysInMonth = lastDayOfMonth.getDate();
    const startingDayOfWeek = firstDayOfMonth.getDay();
    const weekStartDay = config.weekStartDay || 0;

    let offset = startingDayOfWeek - weekStartDay;
    if (offset < 0) offset += 7;

    const days = [];

    for (let i = 0; i < offset; i++) {
      days.push({ day: null, isEmpty: true });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dayData = calendarData?.dailyData?.[day.toString()];
      days.push({
        day,
        isEmpty: false,
        income: dayData?.income || 0,
        expense: dayData?.expense || 0,
        transactions: dayData?.transactions || [],
        isToday: new Date().getDate() === day &&
                 new Date().getMonth() === month - 1 &&
                 new Date().getFullYear() === year
      });
    }

    // Add trailing days from next month to fill the last row
    const remainder = days.length % 7;
    if (remainder > 0) {
      const trailingDays = 7 - remainder;
      for (let i = 1; i <= trailingDays; i++) {
        days.push({ day: i, isEmpty: true, isTrailing: true });
      }
    }

    return days;
  };

  const days = generateCalendarDays();
  const summary = calendarData?.summary || { totalIncome: 0, totalExpense: 0, netBalance: 0, transactionCount: 0 };

  // Get background style for day cells (desktop only)
  const getDayStyle = (dayInfo, isSelected) => {
    if (isSelected) {
      return {
        backgroundColor: 'var(--accent-bg)',
        color: 'var(--accent-text)'
      };
    }

    const hasIncome = dayInfo.income > 0;
    const hasExpense = dayInfo.expense > 0;

    if (hasIncome && hasExpense) {
      return {
        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(239, 68, 68, 0.15))',
        border: '1px solid rgba(139, 92, 246, 0.3)',
        color: '#6b7280'
      };
    } else if (hasIncome) {
      return {
        backgroundColor: `${config.incomeColor}15`,
        border: `1px solid ${config.incomeColor}40`,
        color: config.incomeColor
      };
    } else if (hasExpense) {
      return {
        backgroundColor: `${config.expenseColor}15`,
        border: `1px solid ${config.expenseColor}40`,
        color: config.expenseColor
      };
    }
    return {};
  };

  // Selected day totals for badge
  const selectedDayTotal = selectedDay
    ? (selectedDay.income > 0 && selectedDay.expense > 0
        ? null // show both
        : selectedDay.income > 0
          ? { amount: selectedDay.income, color: config.incomeColor, prefix: '' }
          : selectedDay.expense > 0
            ? { amount: selectedDay.expense, color: config.expenseColor, prefix: '' }
            : null)
    : null;

  return (
    <div className="space-y-3 md:space-y-4 pb-4">
      {/* ========== MOBILE ========== */}
      <div className="xl:hidden">
        {/* Page Title */}
        <StickyPageHeader borderBottom={true}>
          <h1 className="text-lg font-bold text-surface-900">Calendar</h1>
        </StickyPageHeader>

        {/* Compact Month Header with inline summary */}
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={goToPreviousMonth}
            className="w-8 h-8 rounded-lg flex items-center justify-center active:bg-surface-100 transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-surface-500" />
          </button>
          <div className="text-center">
            <h2 className="text-base font-bold text-surface-900">{MONTH_NAMES[month - 1]} {year}</h2>
            <div className="flex items-center justify-center gap-2.5 mt-0.5">
              <span className="text-xs font-semibold" style={{ color: config.incomeColor }}>
                {formatAmount(summary.totalIncome)}
              </span>
              <span className="text-[10px] text-surface-300">|</span>
              <span className="text-xs font-semibold" style={{ color: config.expenseColor }}>
                {formatAmount(summary.totalExpense)}
              </span>
            </div>
          </div>
          <button
            onClick={goToNextMonth}
            className="w-8 h-8 rounded-lg flex items-center justify-center active:bg-surface-100 transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-surface-500" />
          </button>
        </div>

        {/* Today Button */}
        {!isCurrentMonth && (
          <div className="flex justify-center mb-3">
            <button
              onClick={goToToday}
              className="text-xs font-semibold px-4 py-1.5 rounded-full"
              style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
            >
              Go to Today
            </button>
          </div>
        )}

        {/* Calendar Grid */}
        <div
          className="bg-white rounded-2xl px-2 py-2.5 border border-surface-200"
          data-no-swipe="true"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{
            ...getCalendarTransformStyle(),
            transition: isTransitioning ? 'transform 0.2s ease-out, opacity 0.15s ease-out' : 'transform 0.1s ease-out'
          }}
        >
          {/* Day Names Header */}
          <div className="grid grid-cols-7 mb-1">
            {getShortDayNames().map((dayName, idx) => {
              const dow = getDayOfWeekIndex(idx);
              return (
                <div
                  key={idx}
                  className={`text-center text-[11px] font-semibold py-1 ${
                    dow === 0 ? 'text-expense-500' : dow === 6 ? 'text-primary-500' : 'text-surface-400'
                  }`}
                >
                  {dayName}
                </div>
              );
            })}
          </div>

          {/* Calendar Days with dot indicators */}
          <div className="grid grid-cols-7">
            {days.map((dayInfo, index) => {
              const hasIncome = dayInfo.income > 0;
              const hasExpense = dayInfo.expense > 0;
              const isSelected = selectedDay?.day === dayInfo.day && !dayInfo.isEmpty;

              // Trailing days from next month
              if (dayInfo.isTrailing) {
                return (
                  <div key={index} className="flex flex-col items-center justify-center py-2">
                    <span className="text-[13px] text-surface-300">{dayInfo.day}</span>
                    <div className="h-1 mt-0.5" />
                  </div>
                );
              }

              // Empty leading days
              if (dayInfo.isEmpty) {
                return <div key={index} className="py-2" />;
              }

              return (
                <button
                  key={index}
                  onClick={() => handleDayClick(dayInfo.day, dayInfo)}
                  className={`
                    flex flex-col items-center justify-center py-2 rounded-xl transition-all active:scale-95
                    ${dayInfo.isToday && !isSelected ? 'ring-2 ring-primary-400' : ''}
                  `}
                  style={isSelected ? { backgroundColor: 'var(--accent-bg)' } : {}}
                >
                  <span className={`text-[13px] ${
                    isSelected
                      ? 'font-bold'
                      : dayInfo.isToday
                        ? 'font-bold text-primary-600'
                        : 'font-medium text-surface-700'
                  }`}
                    style={isSelected ? { color: 'var(--accent-text)' } : {}}
                  >
                    {dayInfo.day}
                  </span>
                  {/* Dot indicators */}
                  <div className="flex gap-0.5 mt-0.5 h-1">
                    {hasIncome && (
                      <div
                        className="w-1 h-1 rounded-full"
                        style={{ backgroundColor: isSelected ? 'rgba(255,255,255,0.7)' : config.incomeColor }}
                      />
                    )}
                    {hasExpense && (
                      <div
                        className="w-1 h-1 rounded-full"
                        style={{ backgroundColor: isSelected ? 'rgba(255,255,255,0.7)' : config.expenseColor }}
                      />
                    )}
                  </div>
                  {/* Spacer for consistent height when no dots */}
                  {!hasIncome && !hasExpense && <div className="h-1 mt-0.5" />}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-surface-100 px-1">
            <div className="flex flex-col gap-0.5">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: config.incomeColor }} />
                <span className="text-[11px] text-surface-500">Income</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: config.expenseColor }} />
                <span className="text-[11px] text-surface-500">Expense</span>
              </div>
            </div>
            <span className="text-[11px] text-surface-400">Tap a date to see transactions</span>
          </div>
        </div>

        {/* Selected Day Transactions */}
        <div className="mt-3">
          {selectedDay ? (
            <>
              {/* Day header with summary badge */}
              <div className="flex items-center justify-between px-1 mb-2">
                <p className="text-xs font-bold text-surface-900">
                  {selectedDay.day} {MONTH_NAMES[month - 1]}
                  {selectedDay.isToday && <span className="text-primary-500 ml-1 font-medium">(Today)</span>}
                </p>
                <div className="flex items-center gap-1.5">
                  {selectedDay.income > 0 && (
                    <span
                      className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: `${config.incomeColor}15`, color: config.incomeColor }}
                    >
                      {formatAmount(selectedDay.income)}
                    </span>
                  )}
                  {selectedDay.expense > 0 && (
                    <span
                      className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: `${config.expenseColor}15`, color: config.expenseColor }}
                    >
                      {formatAmount(selectedDay.expense)}
                    </span>
                  )}
                </div>
              </div>

              {selectedDay.transactions?.length > 0 ? (
                <div className="bg-white rounded-xl border border-surface-200 overflow-hidden divide-y divide-surface-100">
                  {selectedDay.transactions.map((tx) => {
                    const catInfo = categoryMap[tx.category];
                    const CatIcon = catInfo?.icon ? getIconComponent(catInfo.icon) : null;
                    const catColor = catInfo?.color || (tx.type === 'income' ? config.incomeColor : config.expenseColor);

                    return (
                      <button
                        key={tx._id}
                        onClick={() => handleTransactionClick(tx)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-surface-50 active:bg-surface-100 transition-colors text-left"
                      >
                        <div
                          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${catColor}15` }}
                        >
                          {CatIcon ? (
                            <CatIcon className="w-[18px] h-[18px]" style={{ color: catColor }} />
                          ) : tx.type === 'income' ? (
                            <TrendingUp className="w-4 h-4" style={{ color: config.incomeColor }} />
                          ) : (
                            <TrendingDown className="w-4 h-4" style={{ color: config.expenseColor }} />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-surface-900 truncate">{tx.description}</p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span
                              className="text-[10px] font-medium px-1.5 py-0.5 rounded-md truncate max-w-[120px]"
                              style={{ backgroundColor: `${catColor}15`, color: catColor }}
                            >
                              {tx.category}
                            </span>
                            <span className="text-[11px] text-surface-400 truncate">{tx.bankName}</span>
                          </div>
                        </div>
                        <span
                          className="text-sm font-bold flex-shrink-0"
                          style={{ color: tx.type === 'income' ? config.incomeColor : config.expenseColor }}
                        >
                          {formatAmount(tx.amount)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="bg-white rounded-xl p-6 border border-surface-200 text-center">
                  <Calendar className="w-8 h-8 text-surface-300 mx-auto mb-2" />
                  <p className="text-sm text-surface-500">No transactions on this day</p>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white rounded-xl p-6 border border-surface-200 text-center">
              <Calendar className="w-8 h-8 text-surface-300 mx-auto mb-2" />
              <p className="text-sm text-surface-500">Select a day to view transactions</p>
            </div>
          )}
        </div>
      </div>

      {/* ========== DESKTOP ========== */}

      {/* Header - Desktop only */}
      <div className="page-header hidden xl:block">
        <h1 className="page-title">Calendar</h1>
        <p className="page-subtitle">View your transactions by date</p>
      </div>

      {/* Desktop Monthly Summary */}
      <div className="hidden md:grid xl:grid grid-cols-4 gap-3">
        <div className="bg-white rounded-2xl border border-surface-200 p-4">
          <p className="text-xs text-surface-500 font-medium">Income</p>
          <p className="text-lg font-bold mt-1" style={{ color: config.incomeColor }}>
            {formatAmount(summary.totalIncome)}
          </p>
        </div>
        <div className="bg-white rounded-2xl border border-surface-200 p-4">
          <p className="text-xs text-surface-500 font-medium">Expense</p>
          <p className="text-lg font-bold mt-1" style={{ color: config.expenseColor }}>
            {formatAmount(summary.totalExpense)}
          </p>
        </div>
        <div className="bg-white rounded-2xl border border-surface-200 p-4">
          <p className="text-xs text-surface-500 font-medium">Net Balance</p>
          <p
            className="text-lg font-bold mt-1"
            style={{ color: summary.netBalance >= 0 ? config.incomeColor : config.expenseColor }}
          >
            {formatAmount(summary.netBalance)}
          </p>
        </div>
        <div className="bg-white rounded-2xl border border-surface-200 p-4">
          <p className="text-xs text-surface-500 font-medium">Transactions</p>
          <p className="text-lg font-bold text-surface-900 mt-1">
            {summary.transactionCount}
          </p>
        </div>
      </div>

      {/* Desktop: Full Calendar */}
      <div className="hidden xl:block bg-white rounded-2xl border border-surface-200 overflow-hidden">
        {/* Calendar Header */}
        <div className="flex items-center justify-between p-4 border-b border-surface-100">
          <button
            onClick={goToPreviousMonth}
            className="p-2 rounded-lg hover:bg-surface-100 transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-surface-600" />
          </button>
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-bold text-surface-900">
              {MONTH_NAMES[month - 1]} {year}
            </h2>
            <button
              onClick={goToToday}
              className="px-3 py-1 text-sm font-medium rounded-lg border border-surface-200 hover:bg-surface-50 transition-colors"
            >
              Today
            </button>
          </div>
          <button
            onClick={goToNextMonth}
            className="p-2 rounded-lg hover:bg-surface-100 transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-surface-600" />
          </button>
        </div>

        <div
          className="p-4"
          data-no-swipe="true"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{
            ...getCalendarTransformStyle(),
            transition: isTransitioning ? 'transform 0.2s ease-out, opacity 0.15s ease-out' : 'transform 0.1s ease-out'
          }}
        >
          {/* Day Names Header */}
          <div className="grid grid-cols-7 mb-2">
            {getDayNames().map((dayName, idx) => {
              const dow = getDayOfWeekIndex(idx);
              return (
                <div
                  key={idx}
                  className={`text-center text-xs font-semibold py-2 ${
                    dow === 0 ? 'text-expense-500' : dow === 6 ? 'text-primary-500' : 'text-surface-500'
                  }`}
                >
                  {dayName}
                </div>
              );
            })}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((dayInfo, index) => {
              const hasIncome = dayInfo.income > 0;
              const hasExpense = dayInfo.expense > 0;
              const isSelected = selectedDay?.day === dayInfo.day && !dayInfo.isEmpty;

              // Trailing days from next month
              if (dayInfo.isTrailing) {
                return (
                  <div key={index} className="h-14 flex flex-col items-center justify-center rounded-xl">
                    <span className="text-sm text-surface-300">{dayInfo.day}</span>
                    <div className="h-1.5 mt-0.5" />
                  </div>
                );
              }

              if (dayInfo.isEmpty) {
                return <div key={index} className="h-14" />;
              }

              return (
                <button
                  key={index}
                  onClick={() => handleDayClick(dayInfo.day, dayInfo)}
                  className={`
                    h-14 flex flex-col items-center justify-center rounded-xl transition-all cursor-pointer
                    ${isSelected ? 'shadow-md' : 'hover:bg-surface-50'}
                    ${dayInfo.isToday && !isSelected ? 'ring-2 ring-primary-400' : ''}
                  `}
                  style={isSelected ? { backgroundColor: 'var(--accent-bg)' } : {}}
                >
                  <span className={`text-sm ${
                    isSelected
                      ? 'font-bold'
                      : dayInfo.isToday
                        ? 'font-bold text-primary-600'
                        : 'font-medium text-surface-700'
                  }`}
                    style={isSelected ? { color: 'var(--accent-text)' } : {}}
                  >
                    {dayInfo.day}
                  </span>
                  {/* Dot indicators */}
                  <div className="flex gap-0.5 mt-0.5 h-1.5">
                    {hasIncome && (
                      <div
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ backgroundColor: isSelected ? 'rgba(255,255,255,0.7)' : config.incomeColor }}
                      />
                    )}
                    {hasExpense && (
                      <div
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ backgroundColor: isSelected ? 'rgba(255,255,255,0.7)' : config.expenseColor }}
                      />
                    )}
                  </div>
                  {!hasIncome && !hasExpense && <div className="h-1.5 mt-0.5" />}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-5 mt-3 pt-3 border-t border-surface-100 px-1">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: config.incomeColor }} />
              <span className="text-xs text-surface-500">Income</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: config.expenseColor }} />
              <span className="text-xs text-surface-500">Expense</span>
            </div>
            <span className="text-xs text-surface-400 ml-auto">Click a date to see transactions</span>
          </div>
        </div>
      </div>

      {/* Desktop: Selected Day Details Card */}
      {selectedDay && (
        <div className="hidden xl:block bg-white rounded-2xl border border-surface-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-surface-900">
              {selectedDay.day} {MONTH_NAMES[month - 1]} {year}
              {selectedDay.isToday && <span className="ml-2 text-primary-500 text-sm">(Today)</span>}
            </h3>
            <div className="flex items-center gap-4">
              <div
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
                style={{ backgroundColor: `${config.incomeColor}10` }}
              >
                <TrendingUp className="w-4 h-4" style={{ color: config.incomeColor }} />
                <span className="text-sm font-semibold" style={{ color: config.incomeColor }}>
                  {formatAmount(selectedDay.income)}
                </span>
              </div>
              <div
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
                style={{ backgroundColor: `${config.expenseColor}10` }}
              >
                <TrendingDown className="w-4 h-4" style={{ color: config.expenseColor }} />
                <span className="text-sm font-semibold" style={{ color: config.expenseColor }}>
                  {formatAmount(selectedDay.expense)}
                </span>
              </div>
              <span className="text-sm text-surface-500 bg-surface-100 px-3 py-1.5 rounded-lg">
                {selectedDay.transactions?.length || 0} transaction{(selectedDay.transactions?.length || 0) !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          {selectedDay.transactions?.length > 0 ? (
            <div className="border border-surface-200 rounded-xl overflow-hidden divide-y divide-surface-100 max-h-[300px] overflow-y-auto">
              {selectedDay.transactions.map((tx) => {
                const catInfo = categoryMap[tx.category];
                const CatIcon = catInfo?.icon ? getIconComponent(catInfo.icon) : null;
                const catColor = catInfo?.color || (tx.type === 'income' ? config.incomeColor : config.expenseColor);

                return (
                  <button
                    key={tx._id}
                    onClick={() => handleTransactionClick(tx)}
                    className="w-full flex items-center gap-3 p-3 hover:bg-surface-50 transition-colors text-left"
                  >
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${catColor}15` }}
                    >
                      {CatIcon ? (
                        <CatIcon className="w-5 h-5" style={{ color: catColor }} />
                      ) : tx.type === 'income' ? (
                        <TrendingUp className="w-5 h-5" style={{ color: config.incomeColor }} />
                      ) : (
                        <TrendingDown className="w-5 h-5" style={{ color: config.expenseColor }} />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-surface-800 truncate">{tx.description}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span
                          className="text-[10px] font-medium px-1.5 py-0.5 rounded-md truncate max-w-[140px]"
                          style={{ backgroundColor: `${catColor}15`, color: catColor }}
                        >
                          {tx.category}
                        </span>
                        <span className="text-xs text-surface-400 truncate">{tx.bankName}</span>
                      </div>
                    </div>
                    <span
                      className="font-bold flex-shrink-0"
                      style={{ color: tx.type === 'income' ? config.incomeColor : config.expenseColor }}
                    >
                      {formatAmount(tx.amount)}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Calendar className="w-12 h-12 text-surface-300 mx-auto mb-3" />
              <p className="text-surface-500">No transactions on this day</p>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
